import { useEffect, useState, type KeyboardEvent } from 'react';
import { useDropzone } from 'react-dropzone';

import hexagonPattern from '@/icons/hexagon-geometric.webp';

import { getClaimReceiptDownloadUrl } from '@/services/claims';

import type { ICategory } from '@/types/category';
import type { IClaim, IClaimSubmission } from '@/types/claim';

import { formatCurrency, formatCurrencyForDisplay } from '@/utils/formatting';

export interface EditClaimPayload {
  category: number;
  amount: number;
  details: string;
}

interface ClaimSubmissionFormProps {
  categories: ICategory[];
  currentBalance: number;
  monthlyUsage: number;
  monthlyLimit: number;
  onSubmit: (claimData: IClaimSubmission, file: File) => void;
  mode?: 'new' | 'edit';
  initialClaim?: IClaim | null;
  onSubmitEdit?: (
    payload: EditClaimPayload,
    attachments: { keptReceiptKeys: string[]; newFiles: File[] },
  ) => void | Promise<void>;
  isSubmittingEdit?: boolean;
  onCancelEdit?: () => void;
  initialData?: {
    description?: string;
    categoryId?: number;
    amount?: string;
  };
  preUploadedReceipt?: File;
  onReupload?: () => void;
}

const EXCLUDED_CATEGORY_NAMES = ['X-Team Events', 'Learning', 'Approved Exception'];
/** Minimum claim amount */
const MIN_CLAIM_AMOUNT_CENTS = 100;
const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
};
const MAX_DETAILS_LENGTH = 250;

interface FormErrors {
  category?: string;
  amount?: string;
  receipt?: string;
  details?: string;
}

function getReceiptDisplayName(receipt: string) {
  if (!receipt) return 'No receipt';
  const parts = receipt.split('/');
  const fileName = parts[parts.length - 1];
  return (
    fileName.replace(
      /_\d+_(?:[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|[a-f0-9]{32})$/i,
      '',
    ) || fileName
  );
}

function AttachmentRow({
  name,
  onView,
  onRemove,
}: {
  name: string;
  onView: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700" title={name}>
        {name}
      </span>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onView}
          className="text-sm font-medium text-red-600 hover:text-red-700"
        >
          View
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="text-sm font-medium text-red-600 hover:text-red-700"
        >
          Remove
        </button>
      </div>
    </li>
  );
}

function ClaimSubmissionForm({
  categories,
  currentBalance,
  monthlyUsage,
  monthlyLimit,
  onSubmit,
  mode = 'new',
  initialClaim = null,
  onSubmitEdit,
  isSubmittingEdit = false,
  initialData,
  preUploadedReceipt,
  onReupload,
  onCancelEdit,
}: ClaimSubmissionFormProps) {
  const isEdit = mode === 'edit' && initialClaim;
  const showCancelEdit = Boolean(isEdit && onCancelEdit);

  const [category, setCategory] = useState(() =>
    isEdit && initialClaim
      ? String(initialClaim.category)
      : initialData?.categoryId?.toString() || '',
  );
  const [amount, setAmount] = useState(() =>
    isEdit && initialClaim ? String(initialClaim.amount) : initialData?.amount || '',
  );
  const [receipt, setReceipt] = useState<File | null>(preUploadedReceipt || null);
  const [details, setDetails] = useState(() =>
    isEdit && initialClaim ? (initialClaim.details ?? '') : initialData?.description || '',
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState({
    category: false,
    amount: false,
    receipt: false,
    details: false,
  });
  const existingReceiptKeys =
    isEdit && initialClaim
      ? (initialClaim.receipts ?? (initialClaim.receipt ? [initialClaim.receipt] : []))
      : [];
  const [removedReceiptKeys, setRemovedReceiptKeys] = useState<Set<string>>(new Set());
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    if (isEdit && initialClaim) {
      // oxlint-disable-next-line react/set-state-in-effect
      setCategory(String(initialClaim.category));
      setAmount(String(initialClaim.amount));
      setDetails(initialClaim.details ?? '');
    }
  }, [isEdit, initialClaim]);

  const keptReceiptKeys = existingReceiptKeys.filter((k) => !removedReceiptKeys.has(k));
  const attachedReceiptCount = keptReceiptKeys.length + newFiles.length;
  const hasNoReceipt = attachedReceiptCount === 0;
  const hasExactlyOneReceipt = attachedReceiptCount === 1;

  const hasEditChanges =
    isEdit &&
    initialClaim &&
    (category !== String(initialClaim.category) ||
      Math.abs(parseFloat(amount) - initialClaim.amount) > 1e-6 ||
      details.trim() !== (initialClaim.details ?? '').trim() ||
      removedReceiptKeys.size > 0 ||
      newFiles.length > 0);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0]?.code === 'file-too-large') {
          setErrors((prev) => ({ ...prev, receipt: 'File size must be less than 3MB' }));
        } else if (rejection.errors[0]?.code === 'file-invalid-type') {
          setErrors((prev) => ({
            ...prev,
            receipt: 'Please upload a valid receipt (JPEG/PNG/PDF, max 3MB)',
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            receipt: 'Please upload a valid receipt (JPEG/PNG/PDF, max 3MB)',
          }));
        }
        setReceipt(null);
        return;
      }

      if (acceptedFiles.length > 0) {
        if (isEdit) {
          setNewFiles([acceptedFiles[0]]);
        } else {
          setReceipt(acceptedFiles[0]);
        }
        setErrors((prev) => ({ ...prev, receipt: undefined }));
      }
    },
  });

  const validateCategory = (value: string): string | undefined => {
    if (value === '') {
      return 'Please select a category';
    }
    return undefined;
  };

  const getMaximumClaimableAmount = (forCategoryId: string): number => {
    const selectedCategory = categories.find((cat) => cat.id.toString() === forCategoryId);
    const isCategoryExcluded =
      selectedCategory && EXCLUDED_CATEGORY_NAMES.includes(selectedCategory.name);
    const remainingMonthlyLimit = monthlyLimit - monthlyUsage;
    return isCategoryExcluded ? currentBalance : Math.min(currentBalance, remainingMonthlyLimit);
  };

  const validateAmount = (value: string, categoryId: string): string | undefined => {
    if (value === '') {
      return 'Please enter an amount';
    }

    const numericAmount = parseFloat(value);

    if (isNaN(numericAmount) || numericAmount < 0) {
      return 'Please enter a valid amount';
    }

    if (Math.round(numericAmount * 100) < MIN_CLAIM_AMOUNT_CENTS) {
      return `Amount must be at least ${formatCurrency(MIN_CLAIM_AMOUNT_CENTS / 100, true)}`;
    }

    const maxAmount = getMaximumClaimableAmount(categoryId);

    if (numericAmount > maxAmount) {
      return `Amount exceeds the maximum you can claim right now (${formatCurrency(maxAmount, true)}).`;
    }

    return undefined;
  };

  const validateReceipt = (file: File | null): string | undefined => {
    if (file === null) {
      return 'Please upload a receipt';
    }
    return undefined;
  };

  const validateDetails = (value: string): string | undefined => {
    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      return 'Please enter details';
    }
    if (trimmedValue.length < 5) {
      return 'Details must be at least 5 characters';
    }
    if (value.length > MAX_DETAILS_LENGTH) {
      return `Details must be ${MAX_DETAILS_LENGTH} characters or less`;
    }
    return undefined;
  };

  const handleCategoryBlur = () => {
    setTouched((prev) => ({ ...prev, category: true }));
    const error = validateCategory(category);
    setErrors((prev) => ({ ...prev, category: error }));

    if (amount !== '') {
      const amountError = validateAmount(amount, category);
      setErrors((prev) => ({ ...prev, amount: amountError }));
    }
  };

  // Let increment input by one
  // and have native validation accept values with a precision of a `step` prop
  const handleChangeStep = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      const parsed = parseFloat(amount);
      const current = Number.isFinite(parsed) ? parsed : 0;
      const delta = e.key === 'ArrowUp' ? 1 : -1;
      const minAmount = MIN_CLAIM_AMOUNT_CENTS / 100;

      setAmount(Math.max(minAmount, current + delta).toFixed(2));
    }
  };

  const handleAmountBlur = () => {
    setTouched((prev) => ({ ...prev, amount: true }));
    const error = validateAmount(amount, category);
    setErrors((prev) => ({ ...prev, amount: error }));
  };

  const handleDetailsBlur = () => {
    setTouched((prev) => ({ ...prev, details: true }));
    const error = validateDetails(details);
    setErrors((prev) => ({ ...prev, details: error }));
  };

  const validateEditReceiptCount = (): string | undefined => {
    if (hasNoReceipt) return 'Please upload a receipt';
    if (!hasExactlyOneReceipt) return 'Only one receipt is allowed per claim';
    return undefined;
  };

  const validateAll = (): FormErrors => ({
    category: validateCategory(category),
    amount: validateAmount(amount, category),
    receipt: isEdit ? validateEditReceiptCount() : validateReceipt(receipt),
    details: validateDetails(details),
  });

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({
      category: true,
      amount: true,
      receipt: true,
      details: true,
    });

    const newErrors = validateAll();
    setErrors(newErrors);

    if (newErrors.category || newErrors.amount || newErrors.receipt || newErrors.details) {
      return;
    }

    if (isEdit && onSubmitEdit) {
      setIsSubmittingLocal(true);
      try {
        await onSubmitEdit(
          {
            category: parseInt(category, 10),
            amount: parseFloat(amount),
            details: details.trim(),
          },
          { keptReceiptKeys, newFiles },
        );
      } finally {
        setIsSubmittingLocal(false);
      }
      return;
    }

    if (!receipt) {
      return;
    }

    const claimData: IClaimSubmission = {
      category: parseInt(category, 10),
      amount: parseFloat(amount),
      receiptS3Key: '',
      details: details.trim(),
    };

    onSubmit(claimData, receipt);
  };

  const remainingChars = MAX_DETAILS_LENGTH - details.length;

  const monthlyRemaining = monthlyLimit - monthlyUsage;
  const availableToSpend = Math.min(currentBalance, monthlyRemaining);

  const getLimitingFactor = (): string | null => {
    if (currentBalance < monthlyRemaining) return 'Limited by your total balance.';
    if (monthlyRemaining < currentBalance) return 'Limited by your monthly cap.';
    return null;
  };

  const limitingFactor = getLimitingFactor();

  const getMaxClaimAmount = (): number => getMaximumClaimableAmount(category);

  const { integerPart: availableIntegerPart, decimalPart: availableDecimalPart } =
    formatCurrencyForDisplay(availableToSpend);

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="claim-form">
      {/* Available now */}
      <div className="relative overflow-hidden rounded-xl border border-gray-100 bg-linear-to-br from-white to-rose-50/50 px-6 py-3.5 text-center shadow-soft ring-1 ring-white/60 ring-inset">
        <img
          src={hexagonPattern}
          alt=""
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-[0.06]"
        />
        <div className="pointer-events-none absolute top-1/2 left-1/2 size-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50 blur-2xl"></div>
        <p className="relative mb-1 text-xs font-semibold tracking-wider text-gray-500 uppercase">
          Available now
        </p>
        <p className="relative text-4xl font-bold text-gray-900" data-testid="available-now-amount">
          ${availableIntegerPart}
          <span className="text-xl">.{availableDecimalPart}</span>
        </p>
        {limitingFactor && (
          <p className="relative mt-0.5 mb-1 text-xs text-gray-400">{limitingFactor}</p>
        )}
        <button
          type="button"
          onClick={() => setShowBreakdown((prev) => !prev)}
          className="relative mt-1.5 inline-flex items-center gap-1 text-[11px] text-gray-400 transition-colors hover:text-gray-500"
        >
          How is this calculated?
          <svg
            className={`size-3 transition-transform duration-200 ${showBreakdown ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          className={`relative grid transition-all duration-200 ease-in-out ${showBreakdown ? 'mt-2 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col justify-center gap-1 text-xs text-gray-400 sm:flex-row sm:gap-6">
              <span>
                Total balance:{' '}
                <span className="font-medium text-gray-500">{formatCurrency(currentBalance)}</span>
              </span>
              <span>
                Monthly remaining:{' '}
                <span className="font-medium text-gray-500">
                  {formatCurrency(monthlyRemaining)}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="category" className="mb-2 block text-sm font-medium text-gray-700">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          onBlur={handleCategoryBlur}
          className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2.5 text-base focus:border-transparent focus:ring-2 focus:ring-red-500 focus:outline-hidden"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
            backgroundPosition: 'right 0.5rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1.5em 1.5em',
            paddingRight: '2.5rem',
          }}
        >
          <option value="">Category</option>
          {categories
            .filter(
              (cat) =>
                cat.isActive &&
                // Admin-only categories are hidden, except the one an edited claim already has.
                (cat.userSelectable !== false || (isEdit && cat.id === initialClaim?.category)),
            )
            .map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
        </select>
        {touched.category && errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
        )}
      </div>

      <div>
        <label htmlFor="amount" className="mb-2 block text-sm font-medium text-gray-700">
          Amount (USD) <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <span className="absolute top-2.5 left-3 text-lg text-gray-500">$</span>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={handleChangeStep}
            onBlur={handleAmountBlur}
            step="0.01"
            min="1"
            max={getMaxClaimAmount()}
            className="w-full [appearance:textfield] rounded-md border border-gray-300 py-2 pr-3 pl-8 text-lg focus:border-transparent focus:ring-2 focus:ring-red-500 focus:outline-hidden [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            placeholder="Amount"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">Enter the receipt total converted to USD.</p>
        {touched.amount && errors.amount && (
          <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
        )}
      </div>

      <div>
        <label htmlFor="receipt" className="mb-2 block text-sm font-medium text-gray-700">
          Receipt <span className="text-red-500">*</span>
        </label>
        {isEdit && initialClaim && (
          <ul className="mb-3 space-y-2">
            {keptReceiptKeys.map((s3Key) => (
              <AttachmentRow
                key={s3Key}
                name={getReceiptDisplayName(s3Key)}
                onView={() => {
                  getClaimReceiptDownloadUrl(initialClaim.id, s3Key)
                    .then((r) => window.open(r.downloadUrl, '_blank', 'noopener'))
                    .catch((err) => {
                      console.error('Failed to get receipt download URL:', err);
                    });
                }}
                onRemove={() => setRemovedReceiptKeys((prev) => new Set(prev).add(s3Key))}
              />
            ))}
            {newFiles.map((file, idx) => (
              <li
                key={`new-${idx}-${file.name}`}
                className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">
                  {file.name}
                </span>
                <span className="shrink-0 text-xs text-gray-500">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
                <button
                  type="button"
                  onClick={() => setNewFiles((prev) => prev.filter((_, i) => i !== idx))}
                  className="shrink-0 text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {isEdit && hasNoReceipt && (
          <div
            {...getRootProps()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
              isDragActive
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 hover:border-red-400 hover:bg-gray-50'
            }`}
          >
            <input {...getInputProps()} />
            <p className="text-sm text-gray-500">Click or drag to add receipt</p>
          </div>
        )}
        {!isEdit && receipt && (
          <div className="rounded-lg border-2 border-green-300 bg-green-50 p-6 text-center">
            <div className="mb-2 flex items-center justify-center">
              <svg
                className="mr-2 size-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="font-medium text-green-800">Receipt uploaded</span>
            </div>
            <p className="mb-1 truncate font-semibold text-gray-700" title={receipt.name}>
              {receipt.name}
            </p>
            <p className="text-sm text-gray-500">{(receipt.size / 1024 / 1024).toFixed(2)} MB</p>
            <button
              type="button"
              onClick={() => {
                setReceipt(null);
                setErrors((prev) => ({ ...prev, receipt: undefined }));
                onReupload?.();
              }}
              className="mt-3 text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Remove and upload different file
            </button>
          </div>
        )}
        {!isEdit && !receipt && (
          <div
            {...getRootProps()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
              isDragActive
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 hover:border-red-400 hover:bg-gray-50'
            }`}
          >
            <input {...getInputProps({ id: 'receipt' })} />
            <div className="flex flex-col items-center">
              <svg
                className="mb-3 size-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-sm text-gray-600">Upload receipt</p>
            </div>
          </div>
        )}
        {touched.receipt && errors.receipt && (
          <p className="mt-1 text-sm text-red-600">{errors.receipt}</p>
        )}
      </div>

      <div>
        <label htmlFor="details" className="mb-2 block text-sm font-medium text-gray-700">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="details"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          onBlur={handleDetailsBlur}
          rows={5}
          maxLength={MAX_DETAILS_LENGTH}
          className="w-full resize-none rounded-md border border-gray-300 p-3 text-base focus:border-transparent focus:ring-2 focus:ring-red-500 focus:outline-hidden"
          placeholder="Description"
        />
        <div className="mt-1 flex justify-between">
          <div>
            {touched.details && errors.details && (
              <p className="text-sm text-red-600">{errors.details}</p>
            )}
          </div>
          <p className={`text-sm ${remainingChars < 20 ? 'text-red-600' : 'text-gray-500'}`}>
            {remainingChars} characters remaining
          </p>
        </div>
      </div>

      <p className="text-center text-xs text-gray-400">
        By submitting this claim, you confirm that the receipt attached is accurate and that the
        expense complies with X-Team&apos;s guidelines for Unleash.
      </p>

      <div className="mx-auto flex w-3/4 flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
        {showCancelEdit && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="order-2 flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 transition-colors duration-200 hover:bg-gray-50 sm:order-1"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={
            !!(
              isEdit &&
              (!hasEditChanges ||
                isSubmittingEdit ||
                isSubmittingLocal ||
                !hasExactlyOneReceipt ||
                !category ||
                details.trim().length < 5 ||
                !!validateAmount(amount, category))
            )
          }
          className={`${
            showCancelEdit ? 'order-1 flex-1 sm:order-2' : 'block w-full'
          } rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-600 disabled:bg-gray-400`}
        >
          {isEdit
            ? isSubmittingEdit || isSubmittingLocal
              ? 'Saving...'
              : 'Resubmit'
            : 'Submit Claim'}
        </button>
      </div>
    </form>
  );
}

export { ClaimSubmissionForm };
